export const articles = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/articles`
export const article_tags = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/articles/tags`


export const article_import = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/article_import`

export const article_permissions = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/articles/permissions`

export const article_retag = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/articles/retag`
// Per-row refresh. Distinct from PUT /articles, which re-checks the whole inventory.
export const article_update = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/articles/update`

// FAQ endpoints are per-article, so these are builders rather than constants. The
// article id is the title, which can contain spaces and other characters that must
// be percent-encoded to survive the path.
const api_base = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}`
export const article_url = (articleId) => `${api_base}/articles/${encodeURIComponent(articleId)}`
export const article_faqs_url = (articleId) => `${api_base}/articles/${encodeURIComponent(articleId)}/faqs`
export const article_faq_url = (articleId, questionId) => `${api_base}/articles/${encodeURIComponent(articleId)}/faqs/${encodeURIComponent(questionId)}`
// US1353058: the earlier answers to one question, newest first.
export const article_faq_history_url = (articleId, questionId) => `${article_faq_url(articleId, questionId)}/history`
// The published versions of an article; ?versionId= or ?at= returns a link to the text of one.
export const article_versions_url = (articleId) => `${api_base}/articles/${encodeURIComponent(articleId)}/versions`
export const article_answer_check_url = (articleId) => `${api_base}/articles/${encodeURIComponent(articleId)}/answer-check`
export const article_generate_faqs_url = (articleId) => `${api_base}/articles/${encodeURIComponent(articleId)}/generate-faqs`
export const article_execute_faqs_url = (articleId) => `${api_base}/articles/${encodeURIComponent(articleId)}/execute-faqs`
// The Execute Tests page: one question per POST, answered server-side by the QiC
// Curator answer bot, the same agent manual search uses in the agent workspace.
export const qic_query_url = `${api_base}/qic/query`

// Returns a short-lived presigned S3 URL for an article's stored HTML body, so the
// Curator can preview the rendered article inline (see get-article-content Lambda).
export const article_content = `https://${process.env.REACT_APP_API}.execute-api.${process.env.REACT_APP_REGION}.amazonaws.com/${process.env.REACT_APP_STAGE}/articles/content`

// Knowledge Center base URL. Some articles store a full article URL in articlePath,
// others store only a relative KC path (e.g. "Amazon_Q_Test/Wave_1/Article"); this
// prefix turns the relative ones into working links.
export const kc_base_url = 'https://help.sdge.com'

// Normalizes a pasted Knowledge Center reference to just the article path: strips the
// KC domain and any leading slashes so the field holds "LOB/Intent/Article" whether the
// user pastes a full URL or the bare path. Trailing whitespace is trimmed.
export function stripKcDomain(value) {
    if (!value) return ''
    let v = value.trim()
    if (v.toLowerCase().startsWith(kc_base_url.toLowerCase())) {
        v = v.slice(kc_base_url.length).replace(/^\/+/, '')
    }
    return v
}

// Root of the agent-facing "Agent Assist" MindTouch space.
// Articles are organized into subfolders: First_Set_of_Articles/, Wave_1/, 2026/, etc.
// The /2026/ subfolder is the default for newly curated articles; earlier waves live under
// their own subfolders and cannot be derived automatically.
export const agent_assist_base = 'https://help.sdge.com/AWS_Agent_Assist_Test/'
export const agent_assist_2026_base = 'https://help.sdge.com/AWS_Agent_Assist_Test/2026/'

// Derives an Agent Assist URL assuming the article lives in the /2026/ subfolder.
// Only use this as a placeholder/default — articles in First_Set_of_Articles/ or Wave_1/
// must have their Agent Assist path entered manually.
export function deriveAgentAssistPath(articlePath) {
    if (!articlePath) return ''
    const slug = articlePath.replace(/\/+$/, '').split('/').pop()
    return slug ? `${agent_assist_2026_base}${slug}` : ''
}

// Builds the login link used by the session-expired notification. A path, not
// "/#/login": App.js routes with BrowserRouter, which read the hash form as "/"
// and showed the app again instead of the login page. `origin` is what Login
// reads to send the user back.
function loginLink() {
    return `/login?origin=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`
}

// Turns an axios error into a specific, actionable notification instead of the blanket
// "An error occurred. Please contact your Administrator", so a user can tell a
// permission problem from a validation problem from a genuine outage.
//
// `action` is a short verb phrase describing what failed, e.g. "approve the article".
// Returns { header, message, type } ready to pass straight to showNotification().
//
// Every HTTP failure reports its status code in the header and prefers the API
// response body's own message as the text, falling back to per-status wording only
// when the body carries no message.
//
// Note on 403: the REST authorizer returns a Deny policy for every failure path —
// expired token, malformed token, missing header AND whitelist denial — and API
// Gateway maps all of them to 403. The status code alone therefore cannot tell an
// expired session from a permission denial, so the 403 message covers both and
// offers both recovery paths.
export function describeApiError(error, action = 'complete the request') {
    // No response object at all: network down, DNS, CORS or a timeout. There is no
    // status code or body to report here, so these are the only generic fallbacks.
    if (!error.response) {
        const timedOut = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')
        return timedOut
            ? {
                header: 'Request Timed Out',
                message: `The request timed out while trying to ${action}. It may still be processing — refresh the list before retrying.`,
                type: 'Error',
            }
            : {
                header: 'Cannot Reach Server',
                message: `Could not reach the server to ${action}. Check your network connection and try again.`,
                type: 'Error',
            }
    }

    const status = error.response.status
    const data = error.response.data
    // Our Lambdas and API Gateway both return { message: "..." }; prefer that wording
    // when present since it is the most specific description available. A string body
    // (some API Gateway errors) is used as-is.
    const detail = typeof data === 'string'
        ? data.trim()
        : (data && (data.message || data.Message || data.error)) || ''

    // Per-status fallback wording, used only when the response body has no message.
    let header
    let fallback
    if (status === 401) {
        header = 'Session Expired'
        fallback = `Your session has expired. <a href='${loginLink()}'>Log in again</a> to continue.`
    } else if (status === 403) {
        header = 'Request Denied'
        fallback = `You are not authorized to ${action}. Either your session expired — <a href='${loginLink()}'>log in again</a> — or your account is not on the Curator whitelist for this action.`
    } else if (status === 400) {
        header = 'Invalid Request'
        fallback = `The request to ${action} was rejected as invalid. Check the values you entered and try again.`
    } else if (status === 404) {
        header = 'Not Found'
        fallback = `The item could not be found. It may have been removed — refresh the list.`
    } else if (status === 409) {
        // 409 no longer means only "duplicate title". Approve, delete and
        // reinstate all use it for "the article is not in a state that allows
        // this" — untagged, FAQs still unreviewed, already deleted. Those
        // Lambdas always send their own message, which is used in preference to
        // this fallback, so the header has to stay true for every one of them.
        header = 'Action Not Allowed'
        fallback = `The article is not in a state that allows this yet. Refresh the list to see where it stands.`
    } else if (status === 429) {
        header = 'Too Many Requests'
        fallback = `Too many requests in a short time. Wait a moment and try again.`
    } else if (status === 502 || status === 503 || status === 504) {
        header = 'Upstream Service Failed'
        fallback = `An upstream service failed while trying to ${action}. Try again shortly.`
    } else if (status >= 500) {
        header = 'Server Error'
        fallback = `The server failed while trying to ${action}. Try again; if it keeps happening, contact your Administrator.`
    } else {
        header = 'Request Failed'
        fallback = `Could not ${action}.`
    }

    // A 401 still needs its re-login link even when the body carries a message, since
    // the message alone gives the user no way to recover.
    const message = detail
        ? (status === 401 ? `${detail} <a href='${loginLink()}'>Log in again</a> to continue.` : detail)
        : fallback

    return { header: `${header} (HTTP ${status})`, message, type: 'Error' }
}

export const entra_app_id = process.env.REACT_APP_ENTRA_APP_ID
export const entra_authority = process.env.REACT_APP_ENTRA_AUTHORITY
export const q_assistant_id = process.env.REACT_APP_Q_ASSISTANT_ID
export const ccp_url = process.env.REACT_APP_CCP_URL
export const instance_url = process.env.REACT_APP_INSTANCE_URL
export const connect_login_url = process.env.REACT_APP_CONNECT_LOGIN_URL
export function transformDynamoDBJSON(data) {
    function transformValue(value) {
        if (value === undefined || value === null) return null;
        if (typeof value !== 'object') return value;

        if ('S' in value) return value.S;
        if ('N' in value) return Number(value.N);
        if ('BOOL' in value) return value.BOOL;
        if ('NULL' in value) return null;
        if ('M' in value) return transformMap(value.M);
        if ('L' in value) return value.L.map(transformValue);
        if ('SS' in value) return value.SS;
        if ('NS' in value) return value.NS.map(Number);
        if ('BS' in value) return value.BS;

        return transformMap(value);
    }

    function transformMap(map) {
        const result = {};
        for (const key in map) {
            result[key] = transformValue(map[key]);
        }
        return result;
    }

    // Directly return the transformed Items array
    if (Array.isArray(data.Items)) {
        return data.Items.map(transformMap);
    } else if (Array.isArray(data)) {
        // If the input is already an array, transform it directly
        return data.map(transformMap);
    } else {
        // If it's neither an array nor has an Items property, return an empty array
        console.warn('Unexpected data structure in transformDynamoDBJSON');
        return [];
    }
}